/**
 * Zod Schemas for the etl_integrations digital dispatch API
 *
 * The digital dispatch framework serves every non-GMR resource integration
 * (currently TowPro) through a shared set of endpoints. Responses are
 * camelCase and are returned bare, without the GMR-style status/result
 * envelope. These schemas validate responses at runtime and derive
 * TypeScript types via z.infer.
 */
import { z } from 'zod';

export const DIGITAL_DISPATCH_INTEGRATIONS = {
  TOWPRO: 'towpro',
} as const;

const DigitalDispatchQueueStateSchema = z.enum(['open', 'closed']);
export type DigitalDispatchQueueState = z.infer<
  typeof DigitalDispatchQueueStateSchema
>;

/**
 * Tow reasons accepted by the TowPro create/update payload. The backend
 * restricts the field to these values to conform with TowPro's application.
 */
export const TOWPRO_TOW_REASONS = [
  'Crash',
  'Impound',
  'Hold For Owner',
  'PPI',
  'Repossession',
  'Other',
] as const;
const TowProTowReasonSchema = z.enum(TOWPRO_TOW_REASONS);
export type TowProTowReason = z.infer<typeof TowProTowReasonSchema>;

/** Light covers standard vehicles; Heavy needs a special tow truck. */
export const TOWPRO_SUB_TYPES = ['Light', 'Heavy'] as const;
const TowProSubTypeSchema = z.enum(TOWPRO_SUB_TYPES);
export type TowProSubType = z.infer<typeof TowProSubTypeSchema>;

const TowProAddressInputLocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

const TowProAddressInputSchema = z.object({
  address1: z.string().nullish(),
  country: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  phone: z.string().nullish(),
  location: TowProAddressInputLocationSchema,
});
export type TowProAddressInput = z.infer<typeof TowProAddressInputSchema>;

const TowProVehicleOwnerDataSchema = z.object({
  apt: z.string().nullish(),
  street: z.string().nullish(),
  city: z.string().nullish(),
  zipCode: z.string().nullish(),
  state: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
});
export type TowProVehicleOwnerData = z.infer<
  typeof TowProVehicleOwnerDataSchema
>;

// Request body for POST/PUT /api/towpro/requests. Mirrors the backend
// TowProDispatchRequest model (camelCase aliases).
export const TowProDispatchRequestSchema = z.object({
  regionId: z.number(),
  addressInput: TowProAddressInputSchema,
  reason: TowProTowReasonSchema,
  subType: TowProSubTypeSchema,
  // The tow rotation always determines the assigned tow company, so the
  // backend requires this to be literally true.
  sentToRotation: z.literal(true),
  notes: z.string().nullish(),
  licensePlateNumber: z.string().nullish(),
  licensePlateState: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  color: z.string().nullish(),
  vehicleYear: z.string().nullish(),
  incidentNumber: z.string().nullish(),
  agencyId: z.number().nullish(),
  vin: z.string().nullish(),
  officerName: z.string().nullish(),
  badgeNumber: z.string().nullish(),
  vehicleOwnerData: TowProVehicleOwnerDataSchema.nullish(),
});
export type TowProDispatchRequest = z.infer<typeof TowProDispatchRequestSchema>;

// The request data echoed back on responses also carries the backend-computed
// dispatch typeId, which the frontend never sends.
const TowProDispatchRequestDataSchema = TowProDispatchRequestSchema.extend({
  typeId: z.number().optional(),
});

/**
 * A single ingested TowPro status. The backend does not transform TowPro
 * status payloads into any structured fields yet, so `status` is an open
 * record.
 */
const TowProStatusResponseSchema = z.object({
  id: z.number(),
  requestId: z.number(),
  createdAt: z.string(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO),
  status: z.record(z.unknown()),
});
export type TowProStatusResponse = z.infer<typeof TowProStatusResponseSchema>;

// Response shape shared by the create/update/list endpoints for TowPro
// requests (the digital dispatch DispatchRequestResponse model).
export const TowProRequestResponseSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  callerId: z.string().nullish(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO),
  queueState: DigitalDispatchQueueStateSchema,
  request: z.object({ data: TowProDispatchRequestDataSchema }),
  statuses: z.array(TowProStatusResponseSchema),
});
export type TowProRequestResponse = z.infer<typeof TowProRequestResponseSchema>;

/**
 * GET /api/digital-dispatch/requests returns every integration's requests
 * for the account as a discriminated union keyed on `integration`. Unknown
 * integrations are skipped so this consumer keeps working when new
 * integrations register; malformed TowPro rows fail the parse loudly.
 */
const DigitalDispatchListItemProbeSchema = z
  .object({ integration: z.string() })
  .passthrough();

export function parseTowProRequests(
  response: unknown,
): TowProRequestResponse[] {
  const items = z.array(z.unknown()).parse(response);

  const towProRequests: TowProRequestResponse[] = [];
  for (const item of items) {
    const probe = DigitalDispatchListItemProbeSchema.safeParse(item);
    if (
      !probe.success ||
      probe.data.integration !== DIGITAL_DISPATCH_INTEGRATIONS.TOWPRO
    ) {
      continue;
    }
    towProRequests.push(TowProRequestResponseSchema.parse(item));
  }

  return towProRequests;
}

// Body for POST /api/towpro/requests/:id/cancel. Rotation placement tells
// TowPro where the released vendor re-enters the rotation list.
export const TowProCancelRequestSchema = z.object({
  reason: z.string(),
  rotationPlacement: z.enum(['top', 'bottom']),
});
export type TowProCancelRequest = z.infer<typeof TowProCancelRequestSchema>;

// GET /api/towpro/municipalities response. Regions are pre-filtered by the
// backend to active regions of towing-dispatch submunicipalities.
const TowProRegionSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type TowProRegion = z.infer<typeof TowProRegionSchema>;

const TowProSubMunicipalitySchema = z.object({
  id: z.number(),
  regions: z.array(TowProRegionSchema),
});

export const TowProMunicipalitiesResponseSchema = z.object({
  name: z.string(),
  subMunicipalities: z.array(TowProSubMunicipalitySchema),
});
export type TowProMunicipalitiesResponse = z.infer<
  typeof TowProMunicipalitiesResponseSchema
>;

// GET /api/towpro/vin-lookup/:vin response. Field casing comes from the
// upstream vehicle data provider.
const TowProVinLookupResultSchema = z.object({
  Make: z.string(),
  Model: z.string(),
  ModelYear: z.string(),
  VIN: z.string(),
  VehicleType: z.string(),
});
export type TowProVinLookupResult = z.infer<typeof TowProVinLookupResultSchema>;

export const TowProVinLookupResponseSchema = z.object({
  Count: z.number(),
  Results: z.array(TowProVinLookupResultSchema),
});
export type TowProVinLookupResponse = z.infer<
  typeof TowProVinLookupResponseSchema
>;
